import type { FC } from 'react';
import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types';
import type { AnnotationUpdateEmitter } from '@atlaskit/editor-common/annotation';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/standard-button';
import CommentIcon from '@atlaskit/icon/core/comment';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter.ts';
import { useEmbeddedComments } from '@confluence/embedded-confluence-allowed-features/entry-points/comments.ts';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import { useShortcutStrings } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import {
	TOGGLE_INLINE_COMMENTS_SHORTCUT_RENDERER,
	UNIVERSAL_TOGGLE_INLINE_COMMENTS_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const StyledButton = styled(Button)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&&': {
		minWidth: '30px',
		paddingLeft: token('space.025'),
		paddingRight: token('space.025'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CommentCountWrapper = styled.div({
	display: 'flex',
	flexDirection: 'row',
});

type CommentButtonPlaceholderProps = {
	isDisabled?: boolean;
	handleCommentButtonClick?: (evt: any) => void;
	style?: { padding: string };
	onRender?: () => void;
	contentId?: string;
};

export const CommentButtonPlaceholder: FC<CommentButtonPlaceholderProps> = ({
	isDisabled = true,
	handleCommentButtonClick = () => {},
	style,
	onRender,
}) => {
	useEffect(() => {
		if (!isDisabled) {
			onRender && onRender();
		}
	}, [isDisabled, onRender]);
	const { formatMessage } = useIntl();

	const commentShortcutStrings = useShortcutStrings({
		shortcut: UNIVERSAL_TOGGLE_INLINE_COMMENTS_SHORTCUT,
	});

	const isEmbeddedCommentsEnabled = useEmbeddedComments();
	const eventEmitter: AnnotationUpdateEmitter = getRendererAnnotationEventEmitter();

	if (!isEmbeddedCommentsEnabled) {
		return null;
	} else if (isDisabled) {
		return (
			<StyledButton
				testId="comment-button-placeholder"
				data-test-id="comment-button-placeholder"
				appearance="subtle"
				aria-label={formatMessage({
					id: 'comment-button.icon.label',
					description:
						'Accessible name for the disabled comment button placeholder (icon-only button)',
					defaultMessage: 'Comment button placeholder',
				})}
				iconBefore={<CommentIcon label="" spacing="spacious" />}
				isDisabled
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={style}
			/>
		);
	} else {
		return (
			<>
				<GeneralShortcutListener
					deprecatedAccelerator={TOGGLE_INLINE_COMMENTS_SHORTCUT_RENDERER}
					accelerator={UNIVERSAL_TOGGLE_INLINE_COMMENTS_SHORTCUT}
					listener={(e) => {
						if (document.getElementById('renderer-comments-sidebar')) {
							eventEmitter.emit(AnnotationUpdateEvent.REMOVE_ANNOTATION_FOCUS);
							eventEmitter.emit(AnnotationUpdateEvent.DESELECT_ANNOTATIONS);
						} else {
							handleCommentButtonClick(e);
						}
					}}
				/>
				<Tooltip
					content={
						<FormattedMessage
							id="comment-button.tooltip"
							defaultMessage="Show comments"
							description="Tooltip for button that shows inline comments for a page"
						/>
					}
					shortcut={commentShortcutStrings}
					hideTooltipOnClick
					position="bottom"
				>
					<CommentCountWrapper onClick={handleCommentButtonClick}>
						<StyledButton
							data-id="comment-button-placeholder"
							data-cy="comment-button-placeholder"
							testId="comment-button-placeholder"
							appearance="subtle"
							iconBefore={
								<>
									<CommentIcon
										label={formatMessage({
											id: 'comment-button.icon.label',
											description:
												'aria-label for the comment button to display the first inline comment',
											defaultMessage: 'Show comments',
										})}
										spacing="spacious"
									/>
								</>
							}
							isDisabled={isDisabled}
							// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
							style={style}
						/>
					</CommentCountWrapper>
				</Tooltip>
			</>
		);
	}
};
